/**
 * AP-30-B — Die Systemvorlagen.
 *
 * **Sie bilden den Ist-Zustand ab, nicht die Absicht** (AP-30 3a-#4). Jede
 * Vorlage ist die Antwort auf genau eine Frage: *Welche Endpunkte erreicht diese
 * Rolle am 12. September 2026, durch die Leiter und durch den READONLY-Sonderfall
 * im `RolesGuard`?* Die Umstellung darf keine Sichtbarkeit verschieben — mehr
 * Sicht ist danach ein Häkchen in der Rollenverwaltung, sichtbar und
 * zurücknehmbar. Die beiden Ausnahmen davon stehen weiter unten, jede mit
 * Datum und Begründung.
 *
 * Deshalb stehen hier Dinge, die man sich anders wünschen würde:
 *
 * - `READONLY` trägt `time.entry.read.all`, `EMPLOYEE` nur `time.entry.read.own`.
 *   Das ist keine Verwechslung, sondern der Grund für dieses ganze Paket: „liest
 *   alles, schreibt nichts" ist eine **seitliche** Achse, die in einer Leiter
 *   nicht vorkommt.
 * - `READONLY` trägt **nicht** die rund vierzig Admin-Leseendpunkte. „Liest
 *   alles" war die Absicht; die Rolle steht in keiner einzigen `@Roles`-Liste
 *   und erreicht deshalb nur, was eine Untergrenze von `TIME_BOOKING` hat.
 *
 * ## Zwei benannte Abweichungen (12.9.2026)
 *
 * Die Regel „keine Sichtbarkeit verschieben" gilt für das, was **nebenbei**
 * passiert. Zwei Befunde aus dem Endpunkt-Inventar sind entschieden und deshalb
 * hier geheilt — jeder in seiner eigenen Richtung, jeder benannt:
 *
 * 1. **Stammdaten der Zeiterfassung sind zu.** Die sechs
 *    `time-master-data`-Controller trugen vor AP-30 keine einzige
 *    Rollenprüfung; die Sperre saß allein in der Maske, die immer ADMIN-only
 *    war. `time.project_ref.manage` und `time.user_ref.manage` stehen deshalb
 *    **nur noch in der Administrator-Vorlage**. Wer sie einem Mitarbeiter geben
 *    will, setzt ein Häkchen — das ist der Unterschied zu vorher, wo es jeder
 *    hatte und niemand es sah.
 * 2. **Die Einwilligung ist offen.** `time.privacy.consent` hatte eine
 *    Untergrenze von `EMPLOYEE` und war damit für die Stempeluhr unerreichbar,
 *    obwohl die Einwilligung genau dort abgefragt wird. Sie steht jetzt in
 *    `TIME_BOOKING`. Eine Einwilligung, die man nicht geben kann, ist keine
 *    Sichtbarkeit, die jemand verliert.
 *
 * ## Eine dritte Abweichung (AP-35 Phase A, 17.9.2026)
 *
 * 3. **Projekte liest nicht mehr jeder.** `projects.project.read` stand in
 *    `JEDER` und war damit „alle Projekte für alle". Detailblatt 13 (4.2) teilt
 *    es in `.own` und `.all`: Stempeluhr und Mitarbeiter lesen die eigenen,
 *    Vorgesetzter und „Nur Lesen" alle. Bis Phase B die Felder bringt, an denen
 *    „eigen" hängt (Projektleitung, Zuständige, Einteilung), ist `.own` leer —
 *    die Seite war bisher eine reine Leseliste der Kopfdaten, und die
 *    Mitarbeiter-App liest keine Projekte über dieses Recht. Kopierte Rollen
 *    mit dem alten Namen bekommen in der Migration `.all` und verlieren nichts.
 *
 * Lesen bleibt unberührt: `time.project_ref.read` und `time.user_ref.read`
 * stehen weiter in `JEDER` — ohne sie hätte die Buchungsmaske leere Auswahlen.
 *
 * `SUPER_ADMIN` hat **keine** eigene Vorlage (AP-30 3a-#3): Im Code gehört ihr
 * kein einziger Endpunkt allein; der Unterschied zu `ADMIN` lebt nur in der
 * Betreiber-Konsole, und die hängt am Mandanten. Wer die Rolle heute trägt,
 * bekommt die Administrator-Vorlage.
 *
 * **Feldrechte (`*.view`) stehen in keiner Vorlage** — auch nicht in der
 * Administrator-Vorlage. Sie hätten sonst genau die Sichtbarkeit verschoben, die
 * nicht verschoben werden darf: Die Preisspalte hängt heute an einer
 * Einstellung, nicht an der Rolle (AP-30-F führt beides zusammen).
 *
 * **Eine benannte Ausnahme (18.9.2026, Auftraggeber):** `projects.price.view`
 * steht in der Administrator-Vorlage (`ADMIN_FELDRECHTE`). Das Projektmodul ist
 * neu — es gibt keine alte Einstellung, deren Sichtbarkeit sich verschöbe —, und
 * ohne das Recht sah **niemand** EK, VK, Budget und Haftrücklass, bis jemand ein
 * Häkchen setzte, von dem er nichts wusste. Die übrigen Feldrechte bleiben
 * draußen; eine weitere Ausnahme kommt nur mit eigener Entscheidung dazu.
 */

import { ALL_PERMISSIONS, permissionsOfModule, type Permission } from './permissions'
import { DOCUMENT_PERMISSION_GROUPS } from './documents'

/** Die heutigen Rollen — als Zeichenketten, damit dieses Paket kein Prisma braucht. */
export type LegacyRole =
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'MANAGER'
  | 'EMPLOYEE'
  | 'READONLY'
  | 'TIME_BOOKING'

/**
 * Ist das ein **Feldrecht**?
 *
 * Die Endung `.view` ist die Deklaration, nicht eine Namenskonvention nebenbei:
 * `<modul>.<feld>.view` (AP-30, Abschnitt 4). Ein Feldrecht sperrt kein
 * Endpunkt, sondern ein Feld in der Antwort — es gehört deshalb nicht in eine
 * Vorlage, die aus erreichbaren Endpunkten entsteht.
 */
export function isFieldPermission(permission: string): boolean {
  return permission.endsWith('.view')
}

/** Jedes Recht außer den Feldrechten. */
export const ENDPOINT_PERMISSIONS: readonly Permission[] = ALL_PERMISSIONS.filter(
  (p) => !isFieldPermission(p),
)

export interface RoleTemplate {
  /** Schlüssel der erzeugten Rolle, `TenantRole.key` und `templateKey`. */
  key: string
  /** Welche heutigen Rollen auf diese Vorlage abgebildet werden. */
  legacyRoles: readonly LegacyRole[]
  /** i18n-Schlüssel: `roles.templates.<key>.name` / `.description`. */
  permissions: readonly Permission[]
}

/**
 * Was jeder Angemeldete erreicht, ganz ohne Rolle — die Endpunkte, die heute
 * **keine** `@Roles`-Liste tragen und Lesezugriff sind.
 *
 * Sie sind trotzdem Rechte und keine Ausnahme im Code: Ein Mandant, der seinem
 * Aushilfspersonal die Kundenliste wegnehmen will, soll das können. Dass heute
 * jeder sie sieht, drückt die **Mitgliedschaft in allen fünf Vorlagen** aus,
 * nicht ein fehlendes Schloss. Die sechste, „Lohnverrechnung" (AP-38-D), trägt
 * sie bewusst nicht — siehe dort.
 */
const JEDER: readonly Permission[] = [
  'core.user.read',
  'core.stats.read',
  'time.absence_type.read',
  'time.config.read',
  'time.selection.read',
  'time.user_settings.read.own',
  'time.user_ref.read',
  'time.project_ref.read',
  'time.picklist.read',
  'crm.client.read',
  // AP-28: Der Artikelstamm ist Nachschlagewerk wie die Kundenliste. Das Recht
  // wirkt nur, wo `ARTICLES` gebucht ist — es verschiebt bei keinem
  // Bestandsmandanten eine Sichtbarkeit, denn dort ist das Modul aus.
  'articles.article.read',
]

/**
 * AP-32: Lesen und Schreiben aller Belegarten, abgeleitet aus der Liste der
 * Rechtegruppen — eine neue Art ist damit in beiden Vorlagen, ohne dass jemand
 * sie hier nachträgt.
 */
const BELEGARTEN_LESEN: readonly Permission[] = DOCUMENT_PERMISSION_GROUPS.map(
  (g) => `correspondence.${g}.read` as Permission,
)
const BELEGARTEN_SCHREIBEN: readonly Permission[] = DOCUMENT_PERMISSION_GROUPS.flatMap((g) => [
  `correspondence.${g}.create` as Permission,
  `correspondence.${g}.update` as Permission,
])

/** Was `TIME_BOOKING` (die Stempeluhr) heute erreicht. */
const TIME_BOOKING: readonly Permission[] = [
  ...JEDER,
  // Eigene Buchungen — der Geltungsbereich steckt im Recht.
  'time.entry.read.own',
  'time.entry.create',
  'time.entry.update',
  'time.entry.delete',
  'time.absence.read.own',
  'time.absence.manage',
  'time.daily_record.read.own',
  'time.daily_record.manage',
  'time.report.read.own',
  'time.report.submit',
  // Die Einwilligung wird an der Stempeluhr abgefragt — also muss sie dort auch
  // gegeben werden können (Abweichung 2 im Kopfkommentar).
  'time.privacy.consent',
  // Auftrags-Scan und QR-Marke am Stempelpunkt.
  'time.qr_tag.validate',
  'time.scan.resolve',
  // AP-35 (Blatt 13 4.2, Vorlage „Mitarbeiter"): die Projekte, an denen man
  // arbeitet, und eigene Journal-Einträge. Die PWA-Auftragsmappe braucht kein
  // Projektrecht (Blatt 10). Wirkt nur mit gebuchtem `PROJECT_MGMT.CORE`.
  'projects.project.read.own',
  'projects.journal.manage.own',
  // AP-18 4.: Den Assistenten benutzt jede Vorlage außer „Nur Lesen" (und den drei
  // Zusatzvorlagen, die nur ihr Fachgebiet tragen). Er zeigt nie mehr, als die übrigen Rechte
  // hergeben — jedes Werkzeug prüft sein eigenes. Wirkt nur mit gebuchtem `AI_ASSISTANT`
  // und einem Platz darin.
  'ai.assistant.use',
  // AP-46 4.4: Baustellendokumente erfassen — Regiebericht, Montageprotokoll,
  // Tagesbericht — und die eigenen lesen. Wirkt nur mit gebuchtem Baustein.
  'sitedocs.document.read.own',
  'sitedocs.regie.create',
  'sitedocs.assembly.create',
  'sitedocs.diary.create',
]

/**
 * `EMPLOYEE` — die Stempeluhr und ein wenig mehr.
 *
 * Bis zum 15.10.2026 war die Vorlage **deckungsgleich** mit `TIME_BOOKING`; der
 * einzige Unterschied im ganzen Code war die Einwilligung, und die ist seit dem
 * 12.9.2026 in beiden (Abweichung 2 im Kopfkommentar). Die Vorlage blieb
 * trotzdem stehen, weil sie der Platz ist, an dem die beiden auseinanderlaufen,
 * sobald ein Mitarbeiter mehr darf als ein Stempelpunkt im Flur. Seit AP-46 (4.4)
 * ist das der Fall: Ein Mitarbeiter schreibt Besprechungen und schickt dem Kunden
 * die Kopie eines Baustellendokuments.
 */
const EMPLOYEE: readonly Permission[] = [
  ...TIME_BOOKING,
  'sitedocs.meeting.create',
  'sitedocs.document.send',
]

/** Was `MANAGER` zusätzlich erreicht — die volle Sicht und die Prüfung. */
const MANAGER: readonly Permission[] = [
  ...EMPLOYEE.filter((p) => !p.endsWith('.own')),
  // Der volle Geltungsbereich statt des eigenen. `.all` erfüllt jede
  // Anforderung, die `.own` erfüllt — deshalb steht `.own` hier nicht nochmals.
  'time.entry.read.all',
  'time.absence.read.all',
  'time.daily_record.read.all',
  'time.report.read.all',
  'time.user_settings.read.own',
  // Fremdbuchung: Zeit, Diäten und Fehlzeiten für jemand anderen erfassen.
  'time.booking.for_others',
  'time.entry.read_running',
  'time.entry.review',
  'time.entry.export',
  'time.daily_work_time.read',
  'time.daily_work_time.export',
  'time.report.overview',
  'time.report.manage',
  'time.scan_rule.read',
  'core.user.invite',
  'core.user.update',
  'crm.client.manage',
  // AP-28: Pflege durch ADMIN und MANAGER, wie im Konzept vom 8.9.2026.
  // Endgültiges Löschen und der Import bleiben beim Administrator — beide
  // ändern mehr, als eine Maske zeigt.
  'articles.article.manage',
  'articles.article.export',
  'articles.list.manage',
  // AP-32: Belege schreiben, festschreiben, versenden, Zahlungen erfassen — alle
  // Arten. Löschen von Entwürfen, Stornieren und die Einstellungen (Nummernkreise,
  // Layout) bleiben beim Administrator: Storno und Nummernkreis ändern, was aus
  // dem Haus ist. Das Recht wirkt nur, wo `CORRESPONDENCE` gebucht ist.
  'correspondence.document.read',
  ...BELEGARTEN_LESEN,
  ...BELEGARTEN_SCHREIBEN,
  'correspondence.document.post',
  'correspondence.document.send',
  'correspondence.payment.manage',
  // AP-10 Nachtrag 1, 19.2-9: Wer Belege versendet, darf es aus dem eigenen
  // Postfach tun — ob überhaupt, entscheidet die Richtlinie des Mandanten.
  'core.mail.connect',
  // AP-35 (Blatt 13 4.2): der Vorgesetzte ist die Projektleitung — alles außer
  // den Einstellungen des Moduls. Bausteine, die der Mandant nicht gebucht hat,
  // schneidet `schneideMitModulen` weg; die Rechte hier verschenken nichts.
  'projects.project.read.all',
  'projects.project.manage',
  'projects.position.manage',
  'projects.workstep.manage',
  'projects.approve.execution',
  'projects.assign.manage',
  'projects.journal.manage.all',
  'projects.declaration.issue',
  'projects.certificate.manage',
  'projects.export.run',
  'projects.bom.manage',
  'projects.approve.procurement',
  'projects.sales.manage',
  'projects.billing.run',
  'projects.controlling.read',
  'projects.import.run',
  // AP-37 7.: der Vorgesetzte kalkuliert und gibt ab — alles außer den
  // Einstellungen der Kalkulation. Kosten sehen ist ein Feldrecht und kommt über
  // die Rolle „Kalkulationskosten" (`calculation_kosten`), nicht über die Vorlage.
  'calculation.calculation.read.all',
  'calculation.calculation.manage',
  'calculation.calculation.submit',
  'calculation.lv.import',
  'calculation.lv.export',
  // Detailblatt O 12.: Materialanfragen (A-3).
  'calculation.request.manage',
  // AP-39 4.: der Vorgesetzte legt ab und ordnet zu — die Einstellungen bleiben dem Administrator.
  'inbox.item.read',
  'inbox.item.manage',
  // AP-34 19.: der Vorgesetzte sieht alle Eingangsrechnungen, gibt frei und liest die Projektkosten.
  'incoming_invoices.invoice.read.all',
  'incoming_invoices.approval.decide',
  'incoming_invoices.project_costs.read',
  // KA-35: Der Vorgesetzte stempelt im ERP ohne QR-Code; Mitarbeiter stempeln mit QR in der App.
  'time.qr_tag.exempt',
  // AP-46 4.4: alle Rechte der Baustellendokumente außer den Einstellungen.
  'sitedocs.document.read.all',
  'sitedocs.document.edit_others',
  'sitedocs.document.void',
  'sitedocs.export.run',
  // AP-27 10.: Zuordnungen der Regiezeilen und Verrechnung.
  'sitedocs.regie.assign',
  'sitedocs.regie.bill',
  // AP-47 12.: Mängel vermerken, Bauteile zuordnen.
  'sitedocs.assembly.manage',
]

/**
 * `READONLY` — „liest alles, schreibt nichts", so weit der Code es heute
 * hergibt.
 *
 * Der volle Geltungsbereich beim Lesen ist **kein** Versehen: Die Services
 * schränken nur `EMPLOYEE` und `TIME_BOOKING` auf eigene Datensätze ein, und der
 * Schreibschutz kam aus dem Sonderfall im Guard. Genau diesen Sonderfall ersetzt
 * die Vorlage — sie trägt schlicht kein Schreibrecht. Er kommt zurück, sobald
 * hier aus Bequemlichkeit eines hineingerät.
 */
const READONLY: readonly Permission[] = [
  ...JEDER,
  'time.entry.read.all',
  'time.absence.read.all',
  'time.daily_record.read.all',
  'time.report.read.all',
  // AP-32: „liest alles" — auch die Belege aller Arten. Preise nicht: Das ist
  // ein Feldrecht und steht in keiner Vorlage.
  'correspondence.document.read',
  ...BELEGARTEN_LESEN,
  // AP-35: alle Projekte lesen, nichts schreiben.
  'projects.project.read.all',
  // AP-37: alle Kalkulationen lesen — ohne Kosten (Feldrecht).
  'calculation.calculation.read.all',
  // AP-39: den Posteingang lesen.
  'inbox.item.read',
  // AP-34: alle Eingangsrechnungen und die Projektkosten lesen.
  'incoming_invoices.invoice.read.all',
  'incoming_invoices.project_costs.read',
  // AP-46 4.4: alle Baustellendokumente lesen. Ohne `sitedocs.export.run`, das die
  // Spezifikation hier nennt — „liest alles, schreibt nichts" lässt nur Leserechte
  // zu, und ein Export ist hier nirgends Teil davon (IST-STAND AP-46, Abweichungen).
  'sitedocs.document.read.all',
]

/**
 * Die Feldrechte, die der Administrator trotzdem trägt — jedes mit Datum und
 * Grund im Kopfkommentar. Wirkt wie jedes Recht nur mit gebuchtem Baustein
 * (`schneideMitModulen`).
 */
export const ADMIN_FELDRECHTE: readonly Permission[] = ['projects.price.view' as Permission]

/**
 * `ADMIN` — **alles außer den Feldrechten**, zuzüglich der benannten
 * Ausnahmen in `ADMIN_FELDRECHTE`.
 *
 * Als Liste geschrieben wäre sie am Tag ihrer Entstehung vollständig und mit dem
 * ersten neuen Modul unvollständig. Abgeleitet bleibt sie richtig: Ein Modul,
 * das seine Rechte mitbringt, macht seinen Administrator im selben Zug
 * handlungsfähig.
 */
const ADMIN: readonly Permission[] = [...ENDPOINT_PERMISSIONS, ...ADMIN_FELDRECHTE]

/**
 * AP-38-D — `LOHNVERRECHNUNG`: **alle Rechte der Lohnverrechnung außer
 * `payroll.access.manage` und `payroll.closing.configure`.** Wer die
 * Lohnverrechnung macht, vergibt sie nicht weiter — das entscheidet der Betrieb
 * auf der Seite „Zugriff" — und hebt auch die Sperre fertiggestellter Monate
 * nicht selbst auf (AP-22 Teil 2, G2: Das entscheidet der Administrator der Firma).
 *
 * Die einzige Vorlage **ohne `JEDER`**: Sie ist für die externe
 * Lohnverrechnung gebaut (etwa eine Steuerberatung), die in der Kundenfirma
 * nichts außer der Lohnverrechnung sieht (Entscheidung 8.5) — keine
 * Benutzerliste, keine Kunden, keine Zeiterfassung. Einem Mitarbeiter wird sie
 * **zusätzlich** gegeben; seine übrigen Rollen bleiben.
 *
 * Aus der Registry abgeleitet wie die Administrator-Vorlage: Ein neues Recht
 * der Lohnverrechnung ist ohne Nachtrag darin.
 */
const LOHNVERRECHNUNG: readonly Permission[] = permissionsOfModule('PAYROLL').filter(
  (p) =>
    p !== 'payroll.access.manage' &&
    p !== 'payroll.closing.configure' &&
    // AP-22 Teil 2, I: Ob Zettel an eine KI gehen, entscheidet die Firma, nicht ihr Lohnbüro.
    p !== 'payroll.slip.configure' &&
    !isFieldPermission(p),
)

/**
 * AP-34 19. — drei Vorlagen der Eingangsrechnungen. Wie `LOHNVERRECHNUNG` **ohne
 * `JEDER`**: Sie werden einem Benutzer **zusätzlich** gegeben, Rechte addieren sich.
 *
 * - „Eingangsrechnungen": erfassen, abschließen, Zahlungen von Hand, übergeben.
 * - „Freigabe Eingangsrechnungen": nur die eigenen Aufgaben — für die Projektleitung,
 *   die sonst nichts im Modul tut.
 * - „Zahlungsverkehr": Zahlungslauf anlegen **und** freigeben, Sperre aufheben. Ob
 *   derselbe Mensch beides darf, entscheidet die Einstellung `paymentRunFourEyes`.
 */
const EINGANGSRECHNUNGEN: readonly Permission[] = [
  'incoming_invoices.invoice.read.all',
  'incoming_invoices.invoice.create',
  'incoming_invoices.invoice.update',
  'incoming_invoices.invoice.delete',
  'incoming_invoices.invoice.release',
  'incoming_invoices.invoice.cancel',
  'incoming_invoices.payment.manage',
  'incoming_invoices.export.create',
  'incoming_invoices.project_costs.read',
]

const EINGANGSRECHNUNGEN_FREIGABE: readonly Permission[] = [
  'incoming_invoices.invoice.read.own',
  'incoming_invoices.approval.decide',
  'incoming_invoices.project_costs.read',
]

const ZAHLUNGSVERKEHR: readonly Permission[] = [
  'incoming_invoices.invoice.read.all',
  'incoming_invoices.payment.manage',
  'incoming_invoices.payment_run.create',
  'incoming_invoices.payment_run.release',
  'incoming_invoices.payment.unblock',
]

/**
 * AP-41 Nachtrag 1 — „Vertragsannahme": allein das Recht, die Verträge des Betriebs
 * anzusehen und anzunehmen.
 *
 * **Wer sie trägt, ist als annehmende Person eingetragen** — die Zuweisung der
 * Rolle ist die eine Wahrheit, ein eigenes Merkmal am Benutzer gibt es nicht.
 * Vergeben wird sie über die Karte „Wer nimmt an?" (Einstellungen → Verträge),
 * wie „Lohnverrechnung" über den Schalter auf der Seite „Zugriff"; wer sie in
 * der Rollenverwaltung vergibt, trägt die Person ebenso ein.
 *
 * Wer eingetragen ist, soll annehmen können, ohne Administrator zu sein: die
 * Inhaberin, die sonst nur stempelt, oder die Steuerberatung in einem
 * Testmandanten. Arbeitet eine Eingetragene, nehmen nur die Eingetragenen an
 * (`darfAnnehmen` im Backend, `contracts/vertrags-regeln.ts`).
 */
const VERTRAGSANNAHME: readonly Permission[] = ['core.contract.accept']

export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  { key: 'admin', legacyRoles: ['ADMIN', 'SUPER_ADMIN'], permissions: ADMIN },
  { key: 'manager', legacyRoles: ['MANAGER'], permissions: MANAGER },
  { key: 'employee', legacyRoles: ['EMPLOYEE'], permissions: EMPLOYEE },
  { key: 'time_booking', legacyRoles: ['TIME_BOOKING'], permissions: TIME_BOOKING },
  { key: 'readonly', legacyRoles: ['READONLY'], permissions: READONLY },
  // Keine heutige Rolle führt hierher — die Vorlage ist neu (AP-38-D).
  { key: 'payroll', legacyRoles: [], permissions: LOHNVERRECHNUNG },
  // AP-34 19. — ebenfalls neu, niemandem von selbst zugewiesen.
  { key: 'incoming_invoice_clerk', legacyRoles: [], permissions: EINGANGSRECHNUNGEN },
  { key: 'incoming_invoice_approver', legacyRoles: [], permissions: EINGANGSRECHNUNGEN_FREIGABE },
  { key: 'payments', legacyRoles: [], permissions: ZAHLUNGSVERKEHR },
  // AP-41 Nachtrag 1 — wer sie trägt, ist unter „Wer nimmt an?" eingetragen; nur zusätzlich.
  { key: 'contract_signer', legacyRoles: [], permissions: VERTRAGSANNAHME },
]

export function getRoleTemplate(key: string): RoleTemplate | undefined {
  return ROLE_TEMPLATES.find((t) => t.key === key)
}

/** Welche Vorlage tritt an die Stelle dieser heutigen Rolle? */
export function templateForLegacyRole(role: string): RoleTemplate | undefined {
  return ROLE_TEMPLATES.find((t) => (t.legacyRoles as readonly string[]).includes(role))
}

/**
 * Die Rechte einer Systemvorlage — **die einzige Quelle für eine Systemrolle.**
 *
 * Eine Systemrolle in der Datenbank ist ein **Verweis** auf ihre Vorlage
 * (`templateKey`), kein Abbild: Ihre `permissions`-Spalte bleibt leer. Sonst
 * gäbe es zwei Wahrheiten, und die in der Datenbank wäre ab dem ersten neuen
 * Modul die veraltete — ein Administrator stünde vor einem Endpunkt, den sein
 * Code längst kennt und seine Rolle nicht.
 *
 * Eine **kopierte** Rolle ist das Gegenteil: eine Momentaufnahme, die der
 * Mandant bearbeitet und die sich nicht hinter seinem Rücken ändert.
 */
export function permissionsOfTemplate(templateKey: string): readonly Permission[] {
  return getRoleTemplate(templateKey)?.permissions ?? []
}
